import React, { useCallback, useMemo, useState } from "react"
import { Paragraph } from "../../Components/Paragraph"
import { PRIVACY_POLICY_URL } from "../../constants/textConstants"
import Button from "../../Components/Button"
import { MultiStepFlowContainer } from "../../Components/MultiStepFlow"
import { useOutletContextPlanActions } from "../../hooks/useOutletContextPlanActions"
import { useOutletContextPlan } from "../../hooks/useOutletContextPlan"
import { useNavigate } from "react-router-dom"
import { INVITE_STATUS } from "../../constants/inviteStatus"
import { InviteStatusBadge } from "../../Components/InviteStatusBadge"

/**
 * StudentInviteSent
 *
 * Multi-step flow for inviting a student to the mentorship plan.
 * This is after step 2 of the setup checklist, when the student's email has been sent to them.
 */
const StudentInviteSent = () => {
  const { handleResendStudentInvite, error, loading } = useOutletContextPlanActions()
  const { student, plan } = useOutletContextPlan()
  const [resent, setResent] = useState(false)
  const navigate = useNavigate()
  const handleResendInvite = useCallback(() => {
    handleResendStudentInvite().then(() => setResent(true))
  }, [handleResendStudentInvite])

  const studentAccepted = useMemo(() => student.invitation_status === INVITE_STATUS.ACCEPTED,[student])

  const steps = [
    {
      id: "student-invite-sent",
      encouragement: "🧭",
      encouragementClassName: "text-3xl",
      title: studentAccepted ? "Invite Accepted" : "Invite Sent",
      renderCustomContent: () => {
        return (
          <div className="mx-auto flex max-w-xl flex-col items-center justify-center gap-2 text-center">
            {studentAccepted ? (
              <Paragraph>
                {student?.first_name ?? "Your Student"}{" "}
                has accepted the invitation and can now participate in the Student Activities!
              </Paragraph>
            ): (

            <Paragraph>
              {student?.first_name ?? "Your Student"} will receive an email from us shortly with
              details on how to get started. They can check their junk mail folder if they are
              having trouble finding the email.
            </Paragraph>
            )}
          </div>
        )
      },
      fields: studentAccepted ? null : [
        {
          fieldType: "content",
          content: (
            <div className="mb-8 mt-4 flex flex-col items-center justify-center gap-4">
              <div className="flex w-full items-center justify-center gap-6">
              <InviteStatusBadge
                status={student.invitation_status}
                className={"mx-0 my-0 self-start"}
              />
              <div className="overflow-hidden overflow-ellipsis text-lg font-bold">
                {student.email}
              </div>
              </div>
              {error && (
                <div className="mb-4 rounded-md bg-red-50 p-4 text-left">
                  {error?.message ?? "Uh-oh. We ran into a problem"}
                </div>
              )}
              {resent ? (
                <div>Invite email sent successfully!</div>
              ) : (
                <Button
                  onClick={handleResendInvite}
                  disabled={resent || loading}
                  variant="secondary-outline"
                  width="w-64"
                  type="button"
                >
                  {loading ? "Sending" : "Resend Invite"}
                </Button>
              )}
            </div>
          ),
        },
      ],
    },
  ]

  const handleComplete = useCallback(
    (values, stepId) => {
      navigate("../")
    },
    [navigate],
  )

  const initialValues = useMemo(() => ({}), [])

  return (
    <MultiStepFlowContainer
      steps={steps}
      initialValues={initialValues}
      stepId="invite-student"
      fieldWidth="w-64 mx-auto"
      fieldContainerClassName="flex flex-col items-center"
      customContentAfterButton={
        <div className="text-center text-sm">
          By inviting student, you agree to our{" "}
          <a href={PRIVACY_POLICY_URL} className="text-gray-600 hover:underline">
            Privacy Policy
          </a>
        </div>
      }
      onBack={() => {
        navigate(`/plan/${plan?.token}/checklist`)
        return true
      }}
      alwaysShowBackButton={true}
      showProgress={false}
      onComplete={handleComplete}
      completeButtonText="Back to Checklist"
      completeButtonProps={{
        className: "flex items-center justify-center gap-2",
        width: "w-64",
        beforeIcon: (
          <i className="fa-solid fa-angle-left absolute left-4 top-1/2 -translate-y-1/2 text-3xl"></i>
        ),
      }}
    />
  )
}

export default StudentInviteSent
